/**
 * Doctor panel and replay controls (FR-42, FR-45).
 *
 * Both exist in the UI rather than only in the CLI because this is a hosted web
 * app: a visitor who is told to "run the preflight the day before" has no
 * terminal, and telling them to clone the repo would make FR-45 a requirement
 * only the maintainer can satisfy.
 */

import { el, clear, fmt } from './dom.ts';
import type { AppState, Store } from '../state/store.ts';
import type { DraftPick } from '../core/types.ts';
import { runDoctor, formatDoctorReport, type Check, type DoctorReport } from '../core/doctor.ts';
import { summarizeMatches, type MatchReport } from '../core/matching.ts';
import { playerCacheStatus } from '../state/playerCache.ts';
import { cursorAt, picksThrough, REPLAY_SPEEDS, summarizeVerification, verifyReplay } from '../core/replay.ts';
import { getDraft, getPicks, buildDraftContext, toDraftState } from '../sources/sleeper.ts';

// ---------------------------------------------------------------------------
// Doctor
// ---------------------------------------------------------------------------

export async function renderDoctorPanel(
  host: HTMLElement,
  state: AppState,
  onClose: () => void,
): Promise<void> {
  clear(host);

  const body = el('div', { class: 'doctor-body' }, el('p', {}, 'Running checks…'));
  host.appendChild(
    el(
      'div',
      { class: 'modal doctor-modal', role: 'dialog', 'aria-modal': 'true', 'aria-label': 'Preflight check' },
      el(
        'header',
        { class: 'modal-head' },
        el('h2', {}, 'Preflight check'),
        el('button', { class: 'btn btn-quiet', type: 'button', onClick: onClose, 'aria-label': 'Close' }, '×'),
      ),
      el(
        'p',
        { class: 'hint' },
        'Run this the day before your draft. Anything red is fixable tonight and not during the draft.',
      ),
      body,
    ),
  );

  // Gather what only the running app knows.
  const cache = await playerCacheStatus();

  let sleeperReachable: boolean | null = null;
  try {
    const res = await fetch('https://api.sleeper.app/v1/state/nfl', { credentials: 'omit' });
    sleeperReachable = res.ok;
  } catch {
    sleeperReachable = false;
  }

  let health: Awaited<ReturnType<typeof fetchHealth>> = null;
  health = await fetchHealth();

  // FR-20: match rate per source, computed against the live player index.
  const matchReports: MatchReport[] = [];
  if (state.index) {
    for (const source of state.sources) {
      if (source.kind !== 'user') continue;
      const rows = [
        ...source.rows.map((r) => ({
          raw_name: r.raw_name,
          overall_rank: r.overall_rank,
          team: r.team,
          position: r.position,
          result: { player_key: r.player_key, method: 'exact' as const, score: 1, suggestions: [] },
        })),
        ...source.unmatched.map((u) => ({
          raw_name: u.raw_name,
          overall_rank: u.overall_rank,
          team: u.team,
          position: u.position,
          result: { player_key: null, method: 'none' as const, score: 0, suggestions: u.suggestions },
        })),
      ];
      matchReports.push(summarizeMatches(source.source_id, rows));
    }
  }

  const report = runDoctor({
    league: state.league,
    sources: state.sources,
    sourceDiagnostics: state.sourceReports.map((source) => ({
      source_id: source.source_id,
      source_name: source.source_name,
      status: source.status,
      rows: source.rows,
      message: source.message,
    })),
    blueprint: {
      status: state.blueprintStatus,
      model_version: state.blueprintSnapshot?.model_version ?? null,
      generated_at: state.blueprintSnapshot?.generated_at ?? null,
      expires_at: state.blueprintSnapshot?.expires_at ?? null,
      message: state.blueprintMessage,
    },
    matchReports,
    adp: state.adp,
    playerCache: {
      loaded: cache.loaded,
      count: cache.count,
      age_hours: cache.age_hours,
      stale: cache.stale,
    },
    sleeperReachable,
    health,
    recommendationCount: state.engine?.top.length ?? null,
  });

  renderReport(body, report);
}

async function fetchHealth(): Promise<{ url_fetch_enabled?: boolean; degraded?: string[] } | null> {
  try {
    const res = await fetch('/api/health');
    return res.ok ? ((await res.json()) as { url_fetch_enabled?: boolean; degraded?: string[] }) : null;
  } catch {
    return null;
  }
}

function renderReport(host: HTMLElement, report: DoctorReport): void {
  clear(host);

  host.appendChild(
    el(
      'p',
      { class: `doctor-headline ${report.ok ? 'good' : 'bad'}` },
      report.headline,
    ),
  );

  const list = el('div', { class: 'doctor-checks' });
  for (const check of report.checks) list.appendChild(renderCheck(check));
  host.appendChild(list);

  host.appendChild(
    el(
      'div',
      { class: 'modal-foot' },
      el(
        'button',
        {
          class: 'btn btn-quiet',
          type: 'button',
          onClick: () => void navigator.clipboard?.writeText(formatDoctorReport(report)),
          title: 'Copies the full checklist as plain text',
        },
        'Copy report',
      ),
    ),
  );
}

function renderCheck(check: Check): HTMLElement {
  return el(
    'div',
    { class: `doctor-check ${check.status}` },
    el(
      'div',
      { class: 'doctor-check-head' },
      el('span', { class: `doctor-badge ${check.status}` }, badgeText(check.status)),
      el('span', { class: 'doctor-label' }, check.label),
      el('span', { class: 'doctor-req' }, check.requirement),
    ),
    el('p', { class: 'doctor-detail' }, check.detail),
    check.fix ? el('p', { class: 'doctor-fix' }, check.fix) : null,
  );
}

const badgeText = (status: Check['status']): string =>
  status === 'pass' ? 'OK' : status === 'warn' ? 'WARN' : status === 'fail' ? 'FAIL' : '??';

// ---------------------------------------------------------------------------
// Replay (FR-42)
// ---------------------------------------------------------------------------

export interface ReplayController {
  stop: () => void;
}

export function renderReplayPanel(
  host: HTMLElement,
  store: Store,
  onClose: () => void,
): ReplayController {
  clear(host);

  const state = store.get();
  let timer: ReturnType<typeof setTimeout> | null = null;
  let speedIndex = 1;

  const stop = () => {
    if (timer) clearTimeout(timer);
    timer = null;
  };

  const draftInput = el('input', {
    type: 'text',
    class: 'input',
    placeholder: 'Draft ID of a completed draft',
    value: state.draft?.draft_id ?? '',
  }) as HTMLInputElement;

  const status = el('p', { class: 'replay-status', role: 'status', 'aria-live': 'polite' });
  const scrubber = el('input', {
    type: 'range',
    class: 'replay-scrubber',
    min: '0',
    max: '0',
    value: '0',
    disabled: true,
  }) as HTMLInputElement;
  const log = el('div', { class: 'replay-log' });

  const speedButtons = el('div', { class: 'filter-row' });
  REPLAY_SPEEDS.forEach((s, i) => {
    speedButtons.appendChild(
      el(
        'button',
        {
          class: `chip ${i === speedIndex ? 'active' : ''}`,
          type: 'button',
          onClick: () => {
            speedIndex = i;
            for (const [j, node] of [...speedButtons.children].entries()) {
              node.classList.toggle('active', j === i);
            }
          },
        },
        s.label,
      ),
    );
  });

  const load = async () => {
    const draftId = draftInput.value.trim();
    if (!draftId) return;
    stop();
    clear(log);
    status.textContent = 'Loading draft…';

    try {
      const draft = await getDraft(draftId);
      const picks = await getPicks(draftId);
      if (picks.length === 0) {
        status.textContent = 'That draft has no picks yet — nothing to replay.';
        return;
      }

      const current = store.get();
      const universe = current.universe;
      if (!universe) {
        status.textContent = 'Player list not loaded yet.';
        return;
      }

      const ctx =
        current.draftContext?.draft.draft_id === draftId
          ? current.draftContext
          : buildDraftContext(draft, [], [], current.config.sleeper_user_id);

      const teams = current.league?.teams ?? 12;

      // `verifyReplay` walks `DraftPick`s (what the board holds) while
      // `toDraftState` consumes `SleeperPick`s (what the wire returns). The
      // shapes differ only in that `picked_by` is nullable on the former, so
      // narrowing here keeps replay running through the exact same state
      // derivation the live board uses rather than a lookalike.
      const stateFor = (subset: DraftPick[]) =>
        toDraftState(
          ctx,
          subset.map((p) => ({
            pick_no: p.pick_no,
            round: p.round,
            draft_slot: p.draft_slot,
            roster_id: p.roster_id,
            player_id: p.player_id,
            picked_by: p.picked_by ?? '',
          })),
          [],
          teams,
        );

      const all = toDraftState(ctx, picks, [], teams).picks;

      // Verify before playing: knowing the whole replay is sound up front is
      // more useful than discovering an anomaly at pick 140.
      const verification = verifyReplay(draftId, all, { universe, stateFor });
      status.textContent = summarizeVerification(verification);
      status.className = `replay-status ${verification.ok ? 'good' : 'bad'}`;

      for (const anomaly of verification.anomalies.slice(0, 10)) {
        log.appendChild(
          el('div', { class: 'replay-anomaly' }, `#${anomaly.pick_no} [${anomaly.kind}] ${anomaly.detail}`),
        );
      }

      scrubber.max = String(all.length);
      scrubber.value = '0';
      scrubber.disabled = false;

      const showAt = (index: number) => {
        const cursor = cursorAt(all, index);
        scrubber.value = String(cursor.index);
        const pick = cursor.current;
        if (pick) {
          const player = universe.get(pick.player_id);
          log.prepend(
            el(
              'div',
              { class: 'replay-pick' },
              `#${pick.pick_no} R${pick.round} · ${player?.full_name ?? pick.player_id} (${player?.position ?? '??'}) → ${pick.team_name ?? `slot ${pick.draft_slot}`}`,
            ),
          );
          while (log.childElementCount > 60) log.lastElementChild?.remove();
        }
        // Drive the live board from the replayed state, so replay exercises the
        // real rendering path rather than a parallel one.
        store.update({ draft: stateFor(picksThrough(all, cursor.index)) });
        store.recompute(true);
      };

      scrubber.oninput = () => {
        stop();
        showAt(Number(scrubber.value));
      };

      const play = () => {
        const next = Number(scrubber.value) + 1;
        if (next > all.length) {
          status.textContent = `Replay complete. ${summarizeVerification(verification)}`;
          stop();
          return;
        }
        showAt(next);
        const interval = REPLAY_SPEEDS[speedIndex]?.pickIntervalMs ?? 200;
        timer = setTimeout(play, Math.max(interval, 16));
      };

      playButton.onclick = () => {
        if (timer) {
          stop();
          playButton.textContent = 'Play';
        } else {
          playButton.textContent = 'Pause';
          play();
        }
      };
      playButton.disabled = false;
    } catch (err) {
      status.textContent = err instanceof Error ? err.message : 'Could not load that draft.';
      status.className = 'replay-status bad';
    }
  };

  const playButton = el('button', { class: 'btn', type: 'button', disabled: true }, 'Play') as HTMLButtonElement;

  host.appendChild(
    el(
      'div',
      { class: 'modal replay-modal', role: 'dialog', 'aria-modal': 'true', 'aria-label': 'Replay a draft' },
      el(
        'header',
        { class: 'modal-head' },
        el('h2', {}, 'Replay a completed draft'),
        el(
          'button',
          {
            class: 'btn btn-quiet',
            type: 'button',
            onClick: () => {
              stop();
              onClose();
            },
            'aria-label': 'Close',
          },
          '×',
        ),
      ),
      el(
        'p',
        { class: 'hint' },
        'Steps through a finished draft pick by pick and checks the board at every pick. The best way to confirm the tool behaves before it matters.',
      ),
      draftInput,
      el(
        'div',
        { class: 'filter-row' },
        el('button', { class: 'btn', type: 'button', onClick: () => void load() }, 'Load draft'),
        playButton,
      ),
      speedButtons,
      scrubber,
      status,
      log,
    ),
  );

  return { stop };
}
